import { Edit, FileText, Package, Trash2, Truck, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { typography } from '../../styles/typography';
import { driverApi } from '../Driver/driverApi';
import leaveApi from '../LeaveManagement/leaveApi';
import { getVehicleById } from '../Onboarding/vehicleOnboardingApi';
import { qrApi } from '../QR/QRApi';
import { Card } from '../ui';
import IconTextButton from '../ui/IconTextButton';
import { patrollingApi } from './PatrollingApi';

const PatroleDetails = ({ patrol, onBack }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openOverlay } = useOverlay();
  const [loading, setLoading] = useState(true);
  const [patrolData, setPatrolData] = useState(patrol);
  const [vehicleNames, setVehicleNames] = useState({});
  const [employeeNames, setEmployeeNames] = useState({});
  const [qrSequenceNames, setQrSequenceNames] = useState({});
  const [error, setError] = useState(null);

  // Get patrol_id from URL params or from patrol prop
  const patrolIdParam = searchParams.get('patrolId');
  const patrolId = patrolIdParam || patrol?.patrol_id || patrol?.id;

  // Fetch patrol data from API when component mounts or patrolId changes
  useEffect(() => {
    const fetchPatrolData = async () => {
      if (!patrolId) {
        setError('No patrol ID provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('🔄 Fetching patrol details for ID:', patrolId);
        
        // Fetch fresh patrol data from API
        const freshPatrolData = await patrollingApi.getById(patrolId);
        console.log('✅ Fetched patrol data:', freshPatrolData);
        
        setPatrolData(freshPatrolData);
        
        // Now fetch additional details (vehicles, employees, QR names)
        await fetchPatrolDetails(freshPatrolData);
      } catch (error) {
        console.error('❌ Error fetching patrol data:', error);
        setError(error.message || 'Failed to load patrol details');
        
        // Fallback to using the patrol prop if API fails
        if (patrol) {
          console.log('⚠️ Using fallback patrol data from props');
          setPatrolData(patrol);
          await fetchPatrolDetails(patrol);
        } else {
          setLoading(false);
        }
      }
    };

    fetchPatrolData();
  }, [patrolId]);

  const fetchPatrolDetails = async (patrolToFetch) => {
    try {
      const patrolDetails = patrolToFetch.patrol_details || [];
      const vehicleIds = new Set();
      const employeeIds = new Set();
      
      // Collect all IDs from patrol_details
      patrolDetails.forEach(detail => {
        if (detail.vehicle_id) vehicleIds.add(detail.vehicle_id);
        if (detail.user_ids && Array.isArray(detail.user_ids)) {
          detail.user_ids.forEach(userId => employeeIds.add(userId));
        }
      });

      // Fetch all QR codes with pagination support (needed for both start/end QR and sequence)
      let qrLookup = {};
      try {
        console.log('🔄 Fetching QR codes for lookup...');
        let hasMore = true;
        let lastKey = null;
        let totalFetched = 0;

        // Fetch all pages of QR codes
        while (hasMore) {
          const options = lastKey ? { last_key: lastKey, limit: 100 } : { limit: 100 };
          const qrResponse = await qrApi.getAll(options);
          const qrItems = qrResponse.items || [];
          
          // Create a map of QR IDs to QR details
          qrItems.forEach(qr => {
            if (qr.qr_id) {
              qrLookup[qr.qr_id] = {
                name: qr.name || qr.landmark || qr.qr_id,
                address: qr.landmark || qr.address || 'No address',
              };
            }
          });
          
          totalFetched += qrItems.length;
          
          // Check if there are more pages
          hasMore = qrResponse.has_more || false;
          lastKey = qrResponse.last_key || null;
          
          console.log(`📦 Fetched ${qrItems.length} QR codes (Total: ${totalFetched})`);
        }
        
        console.log(`✅ Loaded ${Object.keys(qrLookup).length} QR codes`);
      } catch (error) {
        console.error('Error fetching QR codes:', error);
      }

      // Enrich patrol data with start and end QR names
      if (patrolToFetch.start_qr_id && qrLookup[patrolToFetch.start_qr_id]) {
        patrolToFetch.startQrName = qrLookup[patrolToFetch.start_qr_id].name;
        patrolToFetch.startQrAddress = qrLookup[patrolToFetch.start_qr_id].address;
      }
      
      if (patrolToFetch.end_qr_id && qrLookup[patrolToFetch.end_qr_id]) {
        patrolToFetch.endQrName = qrLookup[patrolToFetch.end_qr_id].name;
        patrolToFetch.endQrAddress = qrLookup[patrolToFetch.end_qr_id].address;
      }

      // Update the patrol data state with enriched QR info
      setPatrolData({...patrolToFetch});

      // Fetch vehicle names
      const vehicleMap = {};
      if (vehicleIds.size > 0) {
        await Promise.all(
          Array.from(vehicleIds).map(async (vehicleId) => {
            try {
              const vehicle = await getVehicleById(vehicleId);
              vehicleMap[vehicleId] = vehicle?.model_name || vehicleId;
            } catch (error) {
              console.error(`Error fetching vehicle ${vehicleId}:`, error);
              vehicleMap[vehicleId] = vehicleId;
            }
          })
        );
      }

      // Fetch employee names
      const employeeMap = {};
      if (employeeIds.size > 0) {
        await Promise.all(
          Array.from(employeeIds).map(async (employeeId) => {
            try {
              // Try leaveApi first
              const employee = await leaveApi.getUserInfo(employeeId);
              const name = employee?.user_name || employee?.name;
              
              if (name) {
                employeeMap[employeeId] = name;
                console.log(`✅ Fetched employee name from leaveApi for ${employeeId}: ${name}`);
              } else {
                // Fallback to driverApi if leaveApi doesn't return a name
                console.log(`⚠️ No name from leaveApi for ${employeeId}, trying driverApi...`);
                try {
                  const driverDetails = await driverApi.fetchDriverDetails(employeeId);
                  employeeMap[employeeId] = driverDetails?.user_name || driverDetails?.name || 'Unknown Employee';
                  console.log(`✅ Fetched employee name from driverApi for ${employeeId}: ${employeeMap[employeeId]}`);
                } catch (driverError) {
                  console.error(`Error fetching employee ${employeeId} from driverApi:`, driverError);
                  employeeMap[employeeId] = 'Unknown Employee';
                }
              }
            } catch (error) {
              console.error(`Error fetching employee ${employeeId} from leaveApi:`, error);
              // Fallback to driverApi if leaveApi fails
              try {
                console.log(`⚠️ leaveApi failed for ${employeeId}, trying driverApi as fallback...`);
                const driverDetails = await driverApi.fetchDriverDetails(employeeId);
                employeeMap[employeeId] = driverDetails?.user_name || driverDetails?.name || 'Unknown Employee';
                console.log(`✅ Fetched employee name from driverApi (fallback) for ${employeeId}: ${employeeMap[employeeId]}`);
              } catch (driverError) {
                console.error(`Error fetching employee ${employeeId} from driverApi (fallback):`, driverError);
                employeeMap[employeeId] = 'Unknown Employee';
              }
            }
          })
        );
      }

      // Map QR sequence names
      const qrMap = {};
      if (patrolToFetch.qr_sequence && Array.isArray(patrolToFetch.qr_sequence)) {
        patrolToFetch.qr_sequence.forEach(qrId => {
          qrMap[qrId] = qrLookup[qrId]?.name || qrId;
        });
      }

      setVehicleNames(vehicleMap);
      setEmployeeNames(employeeMap);
      setQrSequenceNames(qrMap);
    } catch (error) {
      console.error('Error fetching patrol details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const handleEdit = () => {
    // Check if patrol can be edited (only scheduled patrols)
    const patrolStatus = patrolData?.status;
    if (patrolStatus && patrolStatus !== 'scheduled') {
      alert(`Cannot edit patrol with status "${patrolStatus}". Only "scheduled" patrols can be edited.`);
      return;
    }
    
    // Store patrol data in localStorage for edit mode
    if (patrolData) {
      localStorage.setItem('editPatrolData', JSON.stringify(patrolData));
    }
    
    // Navigate to create patrol schedule with edit mode
    setSearchParams({ view: 'create-patrol', patrolId: patrolData?.patrol_id || patrolId });
  };

  const deletePatrol = async (patrolIdToDelete) => {
    try {
      console.log('🗑️ Deleting patrol:', patrolIdToDelete);
      
      // Call the API to delete the patrol schedule
      const result = await patrollingApi.deletePatrol(patrolIdToDelete);
      
      console.log('✅ Patrol deleted successfully:', result);
      
      // Set flag to trigger refresh in parent component
      sessionStorage.setItem('shouldRefreshPatrolSchedules', 'true');
      
      // Show success message
      alert(result.message || 'Patrol deleted successfully!');
      
      // Navigate back to the schedule list
      setSearchParams({});
      
      // Also call onBack if provided (as a fallback)
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error('❌ Error deleting patrol:', error);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Failed to delete patrol. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleDelete = () => {
    // Check if patrol can be deleted (only scheduled or cancelled patrols)
    const patrolStatus = patrolData?.status;
    if (patrolStatus && !['scheduled', 'cancelled'].includes(patrolStatus)) {
      alert(`Cannot delete patrol with status "${patrolStatus}". Only "scheduled" or "cancelled" patrols can be deleted.`);
      return;
    }
    
    // Show delete warning overlay
    openOverlay('deletePatrolWarning', {
      patrolId: patrolData?.patrol_id || patrolId,
      onConfirm: deletePatrol
    });
  };

  // Show error state
  if (error && !patrolData) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="mb-4">
          <h3 className={typography.h3} style={{ color: '#000000' }}>
            Patrol Convoy Details
          </h3>
        </div>
        <Card.Gradient className="p-6">
          <div className="text-center">
            <div className="text-red-600 font-semibold mb-2">Error Loading Patrol</div>
            <div className="text-gray-600 mb-4">{error}</div>
            <button
              onClick={() => {
                if (onBack) {
                  onBack();
                } else {
                  setSearchParams({});
                }
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Back to List
            </button>
          </div>
        </Card.Gradient>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="mb-4 flex justify-between items-center">
        <h3 className={typography.h3} style={{ color: '#000000', marginBottom: 0 }}>
          Patrol Convoy Details
        </h3>
        <div className="flex gap-3">
          <IconTextButton
            icon={<Edit className="w-6 h-6 text-white" />}
            text="Edit"
            onClick={handleEdit}
            size="md"
            variant="default"
            disabled={
              loading || 
              !patrolData || 
              (patrolData?.status && patrolData.status !== 'scheduled')
            }
            title={
              patrolData?.status && patrolData.status !== 'scheduled'
                ? `Cannot edit ${patrolData.status} patrol`
                : 'Edit patrol'
            }
          />
          <IconTextButton
            icon={<Trash2 className="w-6 h-6" style={{ color: '#ffffff' }} />}
            text="Delete"
            onClick={handleDelete}
            size="md"
            variant="danger"
            disabled={
              loading || 
              !patrolData || 
              (patrolData?.status && !['scheduled', 'cancelled'].includes(patrolData.status))
            }
            title={
              patrolData?.status && !['scheduled', 'cancelled'].includes(patrolData.status)
                ? `Cannot delete ${patrolData.status} patrol`
                : 'Delete patrol'
            }
          />
        </div>
      </div>

      {/* Patrol Information Card */}
      <Card.NoShadowGradient className="mb-6 p-6">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="text-gray-500">Loading patrol information...</div>
          </div>
        ) : patrolData ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {patrolData.patrol_name && (
              <div className="md:col-span-2">
                <div className="text-sm font-semibold text-gray-600 mb-1">Patrol Name</div>
                <div className={typography.h5} style={{ color: '#000000' }}>
                  {patrolData.patrol_name}
                </div>
              </div>
            )}
            <div>
              <div className="text-sm font-semibold text-gray-600 mb-1">Start QR</div>
              <div className={typography.h5} style={{ color: '#000000' }}>
                {patrolData.startQrName || patrolData.start_qr_id || 'Unknown'}
              </div>
              {patrolData.startQrAddress && (
                <div className="text-sm text-gray-600 mt-1">
                  {patrolData.startQrAddress}
                </div>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-600 mb-1">End QR</div>
              <div className={typography.h5} style={{ color: '#000000' }}>
                {patrolData.endQrName || patrolData.end_qr_id || 'Unknown'}
              </div>
              {patrolData.endQrAddress && (
                <div className="text-sm text-gray-600 mt-1">
                  {patrolData.endQrAddress}
                </div>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-600 mb-1">Start Time</div>
              <div className={typography.body1} style={{ color: '#000000' }}>
                {formatTimestamp(patrolData.patrol_start_timestamp)}
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-600 mb-1">End Time</div>
              <div className={typography.body1} style={{ color: '#000000' }}>
                {formatTimestamp(patrolData.patrol_end_timestamp)}
              </div>
            </div>
            {patrolData.qr_sequence && patrolData.qr_sequence.length > 0 && (
              <div className="md:col-span-2">
                <div className="text-sm font-semibold text-gray-600 mb-2">QR Sequence</div>
                <div className="flex flex-wrap gap-2">
                  {patrolData.qr_sequence.map((qrId, index) => (
                    <div
                      key={index}
                      className="px-3 py-1.5 bg-cyan-50 border border-cyan-200 rounded-lg text-sm"
                    >
                      <span className="font-medium text-cyan-700">{index + 1}.</span>{' '}
                      <span className="text-gray-700">{qrSequenceNames[qrId] || qrId}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {patrolData.patrol_type && (
              <div>
                <div className="text-sm font-semibold text-gray-600 mb-1">Patrol Type</div>
                <div className={typography.body1} style={{ color: '#000000' }}>
                  {patrolData.patrol_type === 'on_foot' ? 'On Foot' : patrolData.patrol_type === 'vehicle' ? 'Vehicle' : patrolData.patrol_type}
                </div>
              </div>
            )}
            {patrolData.status && (
              <div>
                <div className="text-sm font-semibold text-gray-600 mb-1">Status</div>
                <div className={typography.body1} style={{ color: '#000000' }}>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    patrolData.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                    patrolData.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                    patrolData.status === 'completed' ? 'bg-green-100 text-green-800' :
                    patrolData.status === 'missed' ? 'bg-red-100 text-red-800' :
                    patrolData.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {patrolData.status.charAt(0).toUpperCase() + patrolData.status.slice(1).replace('_', ' ')}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-gray-500">
            No patrol information available
          </div>
        )}
      </Card.NoShadowGradient>

      {/* Patrol Details Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading patrol details...</div>
          </div>
        ) : !patrolData?.patrol_details || patrolData.patrol_details.length === 0 ? (
          <Card.Gradient className="p-6">
            <div className="text-center text-gray-500">
              No patrol details available
            </div>
          </Card.Gradient>
        ) : (
          <div className="overflow-x-auto">
            <div className="rounded-xl overflow-hidden">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 rounded-tl-xl border-r border-gray-300">
                      S.no
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 border-r border-gray-300">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        Vehicle
                      </div>
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 border-r border-gray-300">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Employees
                      </div>
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 border-r border-gray-300">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Material
                      </div>
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold bg-cyan-100/70 backdrop-blur-sm border-cyan-200/50 rounded-tr-xl">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Notes
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {patrolData.patrol_details.map((detail, index) => {
                    const vehicleId = detail.vehicle_id;
                    const userIds = detail.user_ids || [];
                    const material = detail.material || 'N/A';
                    const notes = detail.note || detail.notes || 'N/A';
                    const isLastRow = index === patrolData.patrol_details.length - 1;
                    const rowBg = index % 2 === 0 ? 'bg-white/50 backdrop-blur-sm' : 'bg-white/70 backdrop-blur-sm';

                    return (
                      <tr key={index}>
                        <td className={`border border-gray-300 px-4 py-3 border-r border-gray-300 ${rowBg} ${isLastRow ? 'rounded-bl-xl' : ''}`}>
                          {index + 1}
                        </td>
                        <td className={`border border-gray-300 px-4 py-3 border-r border-gray-300 ${rowBg}`}>
                          {vehicleId ? (
                            vehicleNames[vehicleId] || vehicleId
                          ) : (
                            <span className="text-gray-400 italic">No Vehicle</span>
                          )}
                        </td>
                        <td className={`border border-gray-300 px-4 py-3 border-r border-gray-300 ${rowBg}`}>
                          {userIds.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {userIds.map((userId, idx) => (
                                <div key={idx} className="text-sm">
                                  {employeeNames[userId] || 'Unknown Employee'}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">No Employees</span>
                          )}
                        </td>
                        <td className={`border border-gray-300 px-4 py-3 border-r border-gray-300 ${rowBg}`}>
                          {material}
                        </td>
                        <td className={`border border-gray-300 px-4 py-3 ${rowBg} ${isLastRow ? 'rounded-br-xl' : ''}`}>
                          {notes}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatroleDetails;
